import { formatInTimeZone } from 'date-fns-tz'
import { useLanguageStore } from '@/stores'
import { da, enUS, nb, fi, sv } from 'date-fns/locale'
import { defaultDK, defaultFI, defaultNO, defaultSE } from './brandMappings'
import { addDays } from 'date-fns'
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  getISOWeek,
  isWeekend,
  startOfWeek
} from 'date-fns'
import type { WeekNumber } from '@/interfaces/weekNumber'
/**
 * In general for dates and time
 * We have agreed to use the format d MMM yyy, HH:mm
 * d for 1 instead of 01 when using MMM as a rule
 * For time we have agreed to use colon : in HH:mm!
 * Try and use what types we have unless you really really need another way of displaying a date
 */
export const formatTimeRange = (
  dateStart: string | Date | undefined,
  dateEnd?: string | Date | undefined,
  timeFormat?: boolean,
  customFormat?: string
): string => {
  if (dateStart !== undefined) {
    // Use app Language for format - as we want it to use the translation for ex. Months, for 'da-DK' - 'June' becomes 'Juni'
    const storageLocale = localStorage.getItem('appLanguageLocale') || 'da-DK'
    const langStore = useLanguageStore()
    const locale: string = langStore.businessCountryLocale()
    const start = new Date(dateStart)
    let formattedString = ''

    if (locale !== undefined) {
      // Format the date for the target timezone - use businessLocale for timezone - so we get the businessCountry timezone
      const timeZone = getTimeZone(locale)
      const format = customFormat ?? 'd MMMM yyyy'
      const startDateFormatted = formatInTimeZone(start, timeZone, format, {
        locale: localeToDateLocale(storageLocale)
      })

      if (timeFormat !== undefined && timeFormat !== false) {
        const startTimeFormatted = formatInTimeZone(start, timeZone, 'HH:mm', {
          locale: localeToDateLocale(storageLocale)
        })
        formattedString = `${startDateFormatted}, ${startTimeFormatted}`
      } else {
        formattedString = `${startDateFormatted}`
      }

      if (dateEnd !== undefined && timeFormat !== undefined && timeFormat !== false) {
        const end = new Date(dateEnd)
        const endTimeFormatted = formatInTimeZone(end, timeZone, 'HH:mm', {
          locale: localeToDateLocale(storageLocale)
        })

        formattedString += ` - ${endTimeFormatted}`
      }

      return formattedString
    }
  }
  return ''
}

export const localeToDateLocale = (locale: string) => {
  switch (true) {
    case defaultDK.locales.includes(locale):
      return da
    case defaultSE.locales.includes(locale):
      return sv
    case defaultNO.locales.includes(locale):
      return nb
    case defaultFI.locales.includes(locale):
      return fi
    default:
      return enUS
  }
}

/**
 *
 * @param bwa abbrevation for before & within & after seen as less then & between two values & higher then
 * @param date the supplied date you wish to check
 * @param time what value you wish to calculate for. written as { milliseconds, seconds, hours, days, years } choose one of them days & hours & years do not need to be used unless you wish to check against a specific hour, day or year.
 * @param hours how many hours you wish to check against can be nothing. Write as whole number eg 7
 * @param days how many days you wish to check against can be nothing. Write as a day so 1-31
 * @param years how many years you wish to check against can be nothing. Write this as a whole year so eg. 3
 *
 * ! Important if multiple checks need to be handled call method again
 * @returns
 */

export const isDateBeforeAfterWithin = (
  bwa: string,
  date: string,
  time: string,
  hours?: number,
  days?: number,
  years?: number
): boolean => {
  // written in miliseconds equal to one of each timestamp
  const hour = 3600000
  const day = 86400000
  const year = 31556952000

  // Convert date to string
  const suppliedDate = new Date(date.toString()).getTime()

  // Get the current date and time
  const currentDate = new Date().getTime()

  // get the positive time in miliseconds between two dates
  const timeInMiliseconds = Math.abs(suppliedDate - currentDate)
  // The difference between the date checked against and the date provided divded by however long one needs to check against
  let targetTime = 0

  //Calculate the difference between the date supplied and today's
  switch (time) {
    case 'hours':
      targetTime = hours! * hour
      break
    case 'days':
      targetTime = days! * day
      break
    case 'years':
      targetTime = years! * year
      break
  }

  switch (bwa) {
    case 'before':
      return targetTime <= timeInMiliseconds
    case 'within':
      return targetTime >= timeInMiliseconds && targetTime <= timeInMiliseconds
    case 'after':
      return targetTime >= timeInMiliseconds
  }

  return false
}

type LocaleTimezoneMap = {
  [locale: string]: string
}

const localeTimezoneMap: LocaleTimezoneMap = {
  'fi-fi': 'Europe/Helsinki',
  'sv-se': 'Europe/Stockholm',
  'da-dk': 'Europe/Copenhagen',
  'nb-no': 'Europe/Oslo',
  'nn-no': 'Europe/Oslo'
}

const getTimeZone = (locale: string | undefined): string => {
  if (!locale) {
    return 'Europe/Copenhagen' // Default value
  }

  // Normalize to lowercase and look up in the map
  return localeTimezoneMap[locale.toLowerCase()] || 'Europe/Copenhagen'
}

export const getFutureWorkdays = (startDate: Date, numberOfWeeks: number): WeekNumber => {
  const adjustedStart = startOfWeek(startDate, { weekStartsOn: 1 }) // Monday
  const adjustedEnd = endOfWeek(addWeeks(adjustedStart, numberOfWeeks - 1), { weekStartsOn: 1 })

  //Removing weekends between start and end dates
  const allDays = eachDayOfInterval({ start: adjustedStart, end: adjustedEnd }).filter(
    (date) => !isWeekend(date)
  )

  // Group days by weekNumber
  const weeksMap = new Map<number, Date[]>()

  allDays.forEach((date) => {
    const weekNumber = getISOWeek(date)

    //Adding week if its not already added to map
    if (!weeksMap.has(weekNumber)) {
      weeksMap.set(weekNumber, [])
    }

    //Get week from map
    const week = weeksMap.get(weekNumber)

    if (week !== undefined) {
      week.push(date)
    }
  })

  return weeksMap
}

export const dateAsUtc = (date: Date): Date => {
  const dateLocal = new Date(date)
  // Calculate the offset from local time to UTC in hours
  const offset = -(dateLocal.getTimezoneOffset() / 60)
  // Convert the start time to UTC by adding the offset
  const utcDate = new Date(dateLocal.getTime() + 3600000 * offset)
  return utcDate
}

// Adds or subtracts days from a date
export const adjustDateByDays = (days: number, date?: Date): Date => {
  if (!date) {
    date = new Date()
  }
  return addDays(date, days)
}

// Locale-specific date format templates
const formatTemplates: { [locale: string]: { [template: string]: string } } = {
  da: {
    short: 'EEEE d. MMM',
    long: "EEEE 'd.' d. MMM yyyy 'kl.' HH.mm"
  }
}

// Returns locale-specific date format strings with appropriate prefixes (d., kl., etc.)
export const getLocalizedDateTimeFormat = (locale: string, template: string = 'long'): string => {
  const localeTemplates = formatTemplates[locale] || formatTemplates['da']
  return localeTemplates[template] || localeTemplates['long']
}
